"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { discountPct } from "@/lib/config/sale";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  Heart,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ShoppingBag,
  Star,
  Minus,
  Plus,
  X,
  Sparkles,
  Cpu,
  Monitor,
  Battery,
  Wifi,
  Camera,
  HardDrive,
  Smartphone,
  Shield,
  Zap,
  Info,
  MessageCircle,
  Ruler,
} from "lucide-react";
import { ShareButton } from "@/components/shared/ShareButton";
import { useCartStore } from "@/lib/store/cartStore";
import { buildVariantId, resolveVariantPrice } from "@/lib/utils/variantPricing";
import { foodPortions, isFoodAvailable, isFoodProduct, isPackPortions, portionPrice, portionVariantId } from "@/lib/config/food";
import { toast } from "@/lib/toast";
import { VirtualTryOnModal } from "./VirtualTryOnModal";
import ReviewCard from "@/app/[domain]/components/reviews/ReviewCard";
import StarRating from "@/app/[domain]/components/reviews/StarRating";
import WriteReviewForm from "@/app/[domain]/components/reviews/WriteReviewForm";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { getOrCreateGuestId, getGuestAvatar, getGuestName } from "@/app/[domain]/components/stories/utils/videoUtils";
import { ShopProductCard } from "@/components/shop/ShopProductCard";
import { ShopProduct } from "@/lib/types/shop";
import { ColorSwatch } from "@/components/ui/ColorSwatch";
import ProductInquiryDialog from "@/components/product/ProductInquiryDialog";
import { HirePanel } from "@/components/product/HirePanel";
import { isForHire, isHireOnly } from "@/lib/config/hire";
import { AddOnsPicker, selectionChoices, selectionExtraPerItem, type AddOnSelection } from "@/components/product/AddOnsPicker";
import { FreeDeliveryBadge, FreeDeliveryNote } from "@/components/product/WebsitePerks";
import { isPaidAddOn, websiteAddOns } from "@/lib/config/websiteAddOns";
import { friendlyError } from "@/lib/utils/error-utils";
import { isWearableProduct, showsClothingSizeGuide } from "./sizeGuide";
import {
  formatMeasurementValue,
  measurementLabel,
  rowsForSize,
  showSizesOnWebsite,
  type MeasurementRow,
} from "@/lib/config/dimensions";
import { colourPhotoAttrs, photosForColour } from "@/lib/config/colourPhotos";
import { ConditionBadge } from "@/components/product/ConditionBadge";
import { pickedElectronicsVariantId, specRowsFor } from "@/lib/config/productSpecs";
import { specIcon } from "./specIcons";

interface ProductVariant {
  _id?: string;
  name?: string;
  type: "size" | "color" | "material" | "style" | "pattern" | "finish" | "weight" | "variants" | "custom";
  value: string;
  price?: number;
  comparePrice?: number;
  stock?: number;
  reservedStock?: number;
  barcode?: string;
  images?: string[];
  description?: string;
  isActive?: boolean;
}

interface ElectronicsAttributes {
  brand?: string;
  model?: string;
  os?: string;
  ram?: string;
  storage?: string;
  processor?: string;
  screenSize?: string;
  screenType?: string;
  resolution?: string;
  battery?: string;
  chargingSpeed?: string;
  chargingPort?: string;
  connectivity?: string[];
  camera?: string[];
  simType?: string;
  refreshRate?: string;
  waterResistance?: string;
  warranty?: string;
  headphoneType?: string;
  driverSize?: string;
  noiseCancellation?: string;
  connectionType?: string[];
  formFactor?: string;
  portCount?: string;
  powerOutput?: string;
  condition?: string;
}

interface ProductDisplayProps {
  product: {
    _id: string;
    name: string;
    slug: string;
    price: number;
    compareAtPrice?: number;
    images: string[];
    variants?: ProductVariant[];
    stock?: number;
    totalStock?: number;
    description?: string;
    isActive?: boolean;
    rating?: number;
    reviewCount?: number;
    composition?: string;
    careInstructions?: string;
    shippingInfo?: string;
    // Its catalogue shelf (null when unset) — the Size Guide stays off Event
    // Decor and flowers shelves (sizeGuide.ts).
    category?: { name: string; slug: string } | null;
    subcategory?: { name: string; slug: string } | null;
    productType?: { name: string; slug: string } | null;
    fashionAttributes?: {
      colors?: Array<{ id: string; name: string; hex: string; images?: any; isMulticolor?: boolean; multicolorList?: string[] }>;
      designs?: Array<{ id: string; name: string }>;
      additionalVariantImages?: Record<string, string[]>;
      sizes?: string[];
      sizeSystem?: string;
      variants?: ProductVariant[];
      composition?: string;
      careInstructions?: string;
    };
    electronicsAttributes?: ElectronicsAttributes;
    // New, Second Hand or the shop's own label (lib/config/productCondition.ts).
    condition?: string;
    hidePrice?: boolean;
    hasCustomPrice?: boolean;
    // The product's own industry slug (the prop below is the layout template).
    industry?: string;
    // Size & measurements, and the shop's choice to show sizes (lib/config/dimensions.ts).
    dimensions?: { rows: MeasurementRow[] };
    showSizesOnWebsite?: boolean;
    hire?: { enabled?: boolean } | null;
    // What it comes with and the extras it offers (products.hireKit), and
    // free delivery on website orders — lib/config/websiteAddOns.ts.
    hireKit?: { id: string; name: string; qty: number; price?: number; productId?: string; dailyRate?: number }[];
    freeDeliveryOnline?: boolean;
  };
  brand: {
    _id: string;
    name: string;
    slug?: string;
    subdomain?: string;
    totalProducts?: number;
    followersCount?: number;
  };
  industry?: "fashion" | "electronics" | "food-beverage";
  relatedProducts?: any[];
  /** "More in <collection>" rows (collections/shop:productCollectionSections). */
  collectionSections?: { _id: string; name: string; slug: string; products: any[] }[];
  shareUrl: string;
  productUrlPrefix?: string;
  backLink?: string;
  backLinkText?: string;
  brandPageUrl?: string;
}

export function ProductDisplay({
  product,
  brand,
  industry = "fashion",
  relatedProducts = [],
  collectionSections = [],
  shareUrl,
  productUrlPrefix = "",
  backLink = "/",
  backLinkText = "SHOP",
  brandPageUrl,
}: ProductDisplayProps) {
  // A card row's product, shaped exactly like the shop grid's (the related
  // and collection rows come back with live stock).
  const toCardProduct = (item: any) => ({
    // Spread the live product so brandId, slug, currency,
    // industry, totalStock/stock/inStock, variants and
    // fashionAttributes all flow through exactly like the shop.
    ...item,
    slug: item.slug || item.name?.toLowerCase().replace(/\s+/g, "-"),
    images: item.images || (item.imageUrl ? [item.imageUrl] : []),
    // The card reads `comparePrice`; the product doc stores it as
    // `compareAtPrice` — map it so the discount badge shows.
    comparePrice: item.comparePrice ?? item.compareAtPrice,
    // Brand info comes from the page context, not the product doc.
    brandName: brand.name,
    brandSlug: brand.slug || brand.subdomain,
    colorVariants:
      item.colorVariants ||
      item.fashionAttributes?.colors?.map((c: any) => ({
        name: c.name,
        hex: c.hex,
      })) ||
      [],
    // Only a product that has a fashion bucket gets one (as the shop row
    // does): an empty one used to draw phones as fashion cards.
    ...(item.fashionAttributes
      ? {
          fashionAttributes: {
            ...item.fashionAttributes,
            sizes: item.fashionAttributes.sizes || [],
            colors: item.fashionAttributes.colors || [],
          },
        }
      : {}),
  });
  // Products already shown under "More in <collection>" aren't repeated below.
  const inSections = new Set(collectionSections.flatMap((s) => s.products.map((p: any) => String(p._id))));
  const moreFromBrand = relatedProducts.filter((p: any) => !inSections.has(String(p._id)));

  // Menu items: portions are the only axis, priced per portion.
  const isFood = industry === "food-beverage" || isFoodProduct(product);
  const isFashion = industry === "fashion" && !isFood;
  const isElectronics = industry === "electronics" && !isFood;
  const foodPortionList = isFood ? foodPortions(product) : [];
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  // A single size is picked from the first render (the effect below repicks it
  // on navigation) — it has no picker to explain a "Choose a Size" flash.
  const [selectedSize, setSelectedSize] = useState<string | null>(() => {
    const sizes: string[] = isElectronics
      ? ((product as any).electronicsAttributes?.storageOptions || [])
      : (product.fashionAttributes?.sizes || []);
    return sizes.length === 1 ? sizes[0] : null;
  });
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const [selectedDesignId, setSelectedDesignId] = useState<string | null>(null);
  const [openAccordion, setOpenAccordion] = useState<number>(0);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [reviewsExpanded, setReviewsExpanded] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isTryOnModalOpen, setIsTryOnModalOpen] = useState(false);
  const [isWritingReview, setIsWritingReview] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  // The extras ticked (piece id → how many with each item), fresh per product.
  const [addOnSelection, setAddOnSelection] = useState<AddOnSelection>({});
  useEffect(() => {
    setAddOnSelection({});
  }, [product._id]);
  // What it comes with and the extras it offers (lib/config/websiteAddOns.ts);
  // the ticked extras add to the button's price, per item.
  const addOnPieces = websiteAddOns(product.hireKit);
  const offersPaidAddOns = addOnPieces.some(isPaidAddOn) && !(product.hidePrice || product.hasCustomPrice);
  const extrasPerItem = offersPaidAddOns ? selectionExtraPerItem(addOnPieces, addOnSelection) : 0;
  const brandScrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Derive options directly — cheap enough that no memoization is needed,
  // and avoids the object-reference instability that caused render cascades.
  // Variant axes: electronics keep theirs in electronicsAttributes (colour +
  // storage/spec tiers); fashion in fashionAttributes (colour + sizes). This
  // makes the colour/storage SELECTORS show for electronics too.
  const ecAttrs: any = (product as any).electronicsAttributes || {};
  const availableSizes: string[] = isFood
    ? foodPortionList.map((p) => p.name)
    : isElectronics
      ? (ecAttrs.storageOptions || [])
      : (product.fashionAttributes?.sizes || []);
  const colorOptions = ((isElectronics ? ecAttrs.colors : product.fashionAttributes?.colors) || []).map((c: any) => ({
    id: c.id,
    name: c.name,
    hex: c.hex,
    isMulticolor: c.isMulticolor,
    multicolorList: c.multicolorList,
  }));
  const designOptions = product.fashionAttributes?.designs || [];

  // Canonical variant id for the CURRENT selection — the single source of truth
  // for BOTH the displayed price and the add-to-cart charge (the server
  // re-resolves the price from this id). Built with buildVariantId, which does
  // NOT lowercase, so it matches the stored mixed-case id (letter sizes
  // S/M/L/XL, storage like "128GB"). resolveVariantPrice falls back to the base
  // product price when the id is undefined or unmatched, so base-priced and
  // partially-selected products are completely unaffected.
  // Electronics name the option as the spec card does: a product with no
  // colours sells its storage tiers on the "default" colour
  // (variant-default-512GB). It used to need a colour, so a colourless
  // phone's tier went into the cart with no option (Nov 2026).
  const selectedColorId: string | undefined =
    selectedColor !== null ? colorOptions[selectedColor]?.id : undefined;
  const currentVariantId: string | undefined = isFood
    ? selectedSize && !isPackPortions(product)
      ? portionVariantId(selectedSize)
      : undefined
    : isElectronics
      ? pickedElectronicsVariantId({
          colorId: selectedColorId,
          hasColours: colorOptions.length > 0,
          size: selectedSize,
          hasSizes: availableSizes.length > 0,
        })
      : buildVariantId(selectedColorId, selectedDesignId, selectedSize);

  // Auto-select single color or size — only run once when the product first loads.
  // Use product._id as the dep so it resets if navigating to a different product.
  useEffect(() => {
    const colorsLen = isElectronics
      ? ((product as any).electronicsAttributes?.colors?.length ?? 0)
      : (product.fashionAttributes?.colors?.length ?? 0);
    if (colorsLen === 1) {
      setSelectedColor(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product._id]);

  useEffect(() => {
    const sizes = isElectronics
      ? ((product as any).electronicsAttributes?.storageOptions || [])
      : (product.fashionAttributes?.sizes || []);
    if (sizes.length === 1) {
      setSelectedSize(sizes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product._id]);


  // Compute display images — depends on the selection and the photos
  // themselves (a joined key), not on every Convex subscription update. A
  // chosen colour's photos follow lib/config/colourPhotos.ts: the first
  // colour's are the main photos, never the stale copy that showed the old
  // "dummy" photo after a replace or AI clean-up (Matcha & Sage).
  const mainImagesKey = (product.images || []).join("|");
  const displayImages = useMemo(() => {
    // Electronics colours have their own photos too (Bagz Phones, Oct 2026):
    // colourPhotoAttrs picks the bucket the product keeps its colours in.
    const chosen = photosForColour(colourPhotoAttrs(product), selectedColorId ?? null, selectedDesignId);
    if (chosen && chosen.length > 0) return chosen;
    return product.images?.length ? product.images : [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColor, selectedDesignId, product._id, mainImagesKey]);


  // Safe current image — clamp index to valid range
  const safeImageIndex = Math.min(selectedImage, Math.max(0, displayImages.length - 1));
  const currentImg = displayImages[safeImageIndex] || "/placeholder.png";

  const { openCart, sessionId } = useCartStore();

  // Use useMutation hook (authenticated WebSocket) for add to cart
  // @ts-ignore
  const addToCartMutation = useMutation(api.cart.core.addToCart);

  // Fetch reviews and stats from Convex
  // @ts-ignore - Suppress recursive type instantiation error
  const reviews = useQuery(
    // @ts-ignore
    api.reviews.queries.getProductReviews,
    brand._id && product._id
      ? {
        brandId: brand._id as Id<"brands">,
        productId: product._id as Id<"products">,
        limit: 50,
      }
      : "skip"
  );

  // @ts-ignore - Suppress recursive type instantiation error
  const reviewStats = useQuery(
    api.reviews.queries.getProductRatingStats,
    brand._id && product._id
      ? {
        brandId: brand._id as Id<"brands">,
        productId: product._id as Id<"products">,
      }
      : "skip"
  );

  // @ts-ignore - Suppress recursive type instantiation error
  const createReview = useMutation(api.reviews.mutations.createReview);
  // @ts-ignore - Suppress recursive type instantiation error
  const voteHelpful = useMutation(api.reviews.mutations.voteReviewHelpful);

  // Note: Brand context is managed by BrandCartSync.tsx
  // We don't need to call setBrandContext here

  // Note: Cart state is now managed by Convex queries in CartDrawer/ShopHeader
  // We don't track "isInCart" state in product pages anymore
  const isInCart = false; // Always show "Add to Cart" button

  // Get review data
  const productReviews = reviews || [];
  const productRating = reviewStats?.averageRating || 0;
  const reviewCount = reviewStats?.totalReviews || 0;

  // Handle review submission
  const handleSubmitReview = async (reviewData: {
    rating: number;
    title: string;
    content: string;
  }) => {
    // Throw rather than return: WriteReviewForm toasts success when this
    // resolves, so a silent return would report a review that was never saved.
    if (!brand._id || !product._id) {
      throw new Error("Unable to submit review");
    }

    try {
      const guestId = getOrCreateGuestId();
      const guestName = getGuestName(guestId);
      const guestAvatar = getGuestAvatar(guestId);

      await createReview({
        brandId: brand._id as Id<"brands">,
        productId: product._id as Id<"products">,
        rating: reviewData.rating,
        title: reviewData.title,
        content: reviewData.content,
        guestId,
        userName: guestName,
        userAvatar: guestAvatar,
      });

      setIsWritingReview(false);
      // WriteReviewForm shows the success toast.
    } catch (error) {
      console.error("Failed to submit review:", error);
      throw error;
    }
  };

  // Handle helpful vote
  const handleVote = async (reviewId: Id<"productReviews">) => {
    try {
      await voteHelpful({ reviewId });
      toast.success("Marked as helpful!");
    } catch (error) {
      console.error("Failed to vote:", error);
      toast.error("Failed to vote");
    }
  };

  // Product details for accordion — only show tabs with actual data, no hardcoded fallbacks
  const productDetails = [
    product.composition ? { title: "Composition", content: product.composition } : null,
    product.careInstructions ? { title: "Care Instructions", content: product.careInstructions } : null,
    product.shippingInfo ? { title: "Shipping & Returns", content: product.shippingInfo } : null,
  ].filter(Boolean) as { title: string; content: string }[];

  // Size & measurements — only when the shop shows sizes on its website; a
  // chosen size's own rows come first (Small: 12 stems), then every size's.
  const measureRows: MeasurementRow[] = showSizesOnWebsite(product)
    ? rowsForSize(product.dimensions?.rows ?? [], selectedSize)
    : [];

  // Electronics specifications (lib/config/productSpecs.ts): the product's;
  // a spec its options decide shows the options, then the one picked (a
  // phone sold in 256GB…2TB never says 256 GB on a 2TB); a picked option's
  // own specs and model number; then the shop's own details. The option
  // picked is the one the price and the cart use (currentVariantId).
  const specVariantId = isElectronics ? currentVariantId : undefined;
  const specEntries = specRowsFor(product, { variantId: specVariantId, size: isElectronics ? selectedSize : null }).map(
    (row) => ({ ...row, icon: specIcon(row.key) }),
  );

  // Helper to get hex color from color name
  function getColorHex(colorName: string): string {
    const colorMap: Record<string, string> = {
      black: "#000000",
      white: "#FFFFFF",
      red: "#DC2626",
      blue: "#2563EB",
      green: "#16A34A",
      yellow: "#EAB308",
      pink: "#EC4899",
      purple: "#9333EA",
      orange: "#EA580C",
      gray: "#6B7280",
      grey: "#6B7280",
      brown: "#92400E",
      navy: "#1E3A5F",
      beige: "#D4C4A8",
      cream: "#FFFDD0",
      gold: "#D4AF37",
      silver: "#C0C0C0",
    };
    return colorMap[colorName?.toLowerCase()] || "#333333";
  }

  // Derive current variant from selections — use useMemo, NOT useEffect+setState
  // (useEffect+setState on colorOptions caused an infinite render loop that froze the UI)
  const currentVariant = useMemo(() => {
    if (!product.variants) return null;
    const colors = product.fashionAttributes?.colors || [];
    const colorName = selectedColor !== null ? colors[selectedColor]?.name : undefined;
    const colorId   = selectedColor !== null ? colors[selectedColor]?.id   : undefined;

    let variant: ProductVariant | undefined;

    // 3-way match: Size + Color + Design
    if (selectedSize && colorId && selectedDesignId) {
      const targetId = `variant-${colorId}-${selectedDesignId}-${selectedSize}`.toLowerCase();
      variant = product.variants.find(v => v._id === targetId || v.barcode === targetId);
    }
    // Size + Color
    if (!variant && selectedSize && colorName) {
      variant = product.variants.find(v => v.type === "variants" && v.value === `${selectedSize} / ${colorName}`);
    }
    // Size only
    if (!variant && selectedSize) {
      variant = product.variants.find(v => v.type === "size" && v.value === selectedSize);
    }
    // Color only
    if (!variant && colorName) {
      variant = product.variants.find(v => v.type === "color" && v.value === colorName);
    }
    return variant || null;
  }, [selectedSize, selectedColor, selectedDesignId, product.variants, product._id]);

  const handleColorSelect = (index: number) => {
    setSelectedColor(index);
    // Always reset to first image when color changes so we see this color's first image
    setSelectedImage(0);
  };

  const handleDesignSelect = (id: string | null) => {
    setSelectedDesignId(id);
    const colorId = selectedColor !== null ? colorOptions[selectedColor]?.id : undefined;

    // Switch to first image of this look if available
    if (colorId && id) {
      const lookId = `${colorId}:${id}`;
      const lookSlots = product.fashionAttributes?.additionalVariantImages?.[lookId];
      if (lookSlots && lookSlots.length > 0) {
        setSelectedImage(0);
      }
    }
  };

  // Price for the current selection — resolved from the canonical variant id
  // (returns the per-variant price when set, else the base product price). This
  // replaces the old currentVariant lookup that matched on fields absent from
  // the Convex data (_id/type/value/barcode) and so always fell back to base.
  // A menu portion is priced by its name (pack portions have no variant to price from).
  const getCurrentPrice = () =>
    (isFood && selectedSize ? portionPrice(product, selectedSize) : null) ?? resolveVariantPrice(product, currentVariantId);

  const getCurrentStock = () => {
    if (currentVariant?.stock !== undefined && currentVariant.stock !== null) {
      return currentVariant.stock;
    }
    return product.totalStock || product.stock || 0;
  };

  const scrollBrandLeft = () => {
    brandScrollContainerRef.current?.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollBrandRight = () => {
    brandScrollContainerRef.current?.scrollBy({ left: 300, behavior: "smooth" });
  };

  const handleAddToCart = async () => {
    // Smart validation with helpful prompts
    if (colorOptions.length > 0 && selectedColor === null) {
      toast.error("Please select a color to continue");
      return;
    }
    if (designOptions.length > 0 && !selectedDesignId) {
      toast.error("Please select a design to continue");
      return;
    }
    if (availableSizes.length > 0 && !selectedSize) {
      toast.error("Please select a size to continue");
      return;
    }

    setIsAdding(true);

    const selectedColorOption = selectedColor !== null ? colorOptions[selectedColor] : null;
    const colorName = selectedColorOption?.name;
    const colorHex = selectedColorOption?.hex;
    const isMulticolor = selectedColorOption?.isMulticolor;
    const multicolorList = selectedColorOption?.multicolorList;

    const cartVariants = {
      ...(selectedSize && { size: selectedSize }),
      ...(colorName && { color: colorName }),
      ...(colorHex && { colorHex }),
      ...(isMulticolor && { isMulticolor }),
      ...(multicolorList && { multicolorList }),
    };

    // Canonical variant id for the current selection — the SAME id whose price
    // the display resolves (unifies fashion + electronics via buildVariantId,
    // with NO lowercasing so mixed-case sizes like S/M/L/XL and storage like
    // "128GB" match the stored id). The server re-resolves the charge from it.
    const specificVariantId = currentVariantId;

    try {
      // Use useMutation hook (authenticated WebSocket) — same as CartDrawer fix
      await addToCartMutation({
        productId: product._id as Id<"products">,
        brandId: brand._id as Id<"brands">,
        quantity,
        variantId: specificVariantId,
        selectedVariants: Object.keys(cartVariants).length > 0 ? cartVariants : undefined,
        sessionId: sessionId || undefined,
        // The extras ticked (ids and counts — the server prices them). Sent
        // only when the product offers any, so it replaces the line's choice.
        addOns: offersPaidAddOns ? selectionChoices(addOnSelection) : undefined,
      });
      toast.success("Added to cart!");
      openCart();
    } catch (error: any) {
      console.error("Add to cart error:", error);
      toast.error(friendlyError(error, "Failed to add to cart"));
    } finally {
      setIsAdding(false);
    }
  };

  const stockStatus = getCurrentStock() > 0;
  // Pre-order: a flagged product is purchasable even at 0 stock — recorded as a
  // pre-order at checkout (stock allocated when it arrives).
  const isPreorder = !!(product as any).preorder?.enabled;
  // Made-to-order menu items never sell out; "off menu today" does hide them.
  const untracked = (product as any).trackStock === false;
  const offMenu = isFood && !isFoodAvailable(product);
  const canPurchase = (stockStatus || isPreorder || untracked) && !offMenu;

  // Price visibility flags
  const shouldHidePrice = product.hidePrice || product.hasCustomPrice;
  // For hire (2026-09): a hire-only item is never added to the cart — the hire
  // panel below carries the rates and "Request to hire" instead.
  const hireOnlyItem = isHireOnly(product);

  // Sizes on the website (Events & Decor plan, phase 0 — a per-product switch
  // comes later): one size is no choice, so it gets no picker (it is still
  // auto-picked above); the Size Guide is a clothing table, so only clothing
  // and shoe sizes get it — never flowers, decor or perfume volumes.
  const singleSize = isFashion && availableSizes.length === 1;
  const showSizeGuide = isFashion && showsClothingSizeGuide(product);
  // Try-On dresses a photo in the item — never flowers, balloons or decor.
  const canTryOn = isFashion && isWearableProduct(product);

  return (
    <div className="min-h-screen text-black font-sans selection:bg-black selection:text-white" style={{ backgroundColor: "#FBFCFD" }}>
      {/* Size Guide Modal — clothing and shoe sizes only. Centred, and
          clickable on phones too (the wrapper was pointer-events-none below
          sm, so the close button and backdrop ignored taps). */}
      {showSizeGuide && isSizeGuideOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Size Guide">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={() => setIsSizeGuideOpen(false)}
          />
          <div className="relative bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100 animate-in fade-in zoom-in duration-300">
            <div className="p-6 md:p-12">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h3 className="text-2xl font-bold uppercase tracking-widest mb-2">Size Guide</h3>
                  <p className="text-gray-500 font-light text-sm">Comprehensive measurements for the perfect fit.</p>
                </div>
                <button
                  onClick={() => setIsSizeGuideOpen(false)}
                  aria-label="Close size guide"
                  className="p-2 hover:bg-gray-100 transition-colors rounded-full"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                  <thead className="bg-zinc-50 text-xs uppercase font-bold tracking-wider">
                    <tr>
                      <th className="p-4 border-b border-gray-100">Size</th>
                      <th className="p-4 border-b border-gray-100">US</th>
                      <th className="p-4 border-b border-gray-100">UK</th>
                      <th className="p-4 border-b border-gray-100">EU</th>
                      <th className="p-4 border-b border-gray-100">Bust (in)</th>
                      <th className="p-4 border-b border-gray-100">Waist (in)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-600 font-light">
                    <tr><td className="p-4 font-bold text-black">XS</td><td className="p-4">0-2</td><td className="p-4">4-6</td><td className="p-4">32-34</td><td className="p-4">31-33</td><td className="p-4">23-25</td></tr>
                    <tr><td className="p-4 font-bold text-black">S</td><td className="p-4">4-6</td><td className="p-4">8-10</td><td className="p-4">36-38</td><td className="p-4">33-35</td><td className="p-4">25-27</td></tr>
                    <tr><td className="p-4 font-bold text-black">M</td><td className="p-4">8-10</td><td className="p-4">12-14</td><td className="p-4">40-42</td><td className="p-4">35-37</td><td className="p-4">27-29</td></tr>
                    <tr><td className="p-4 font-bold text-black">L</td><td className="p-4">12-14</td><td className="p-4">16-18</td><td className="p-4">44-46</td><td className="p-4">37-40</td><td className="p-4">29-32</td></tr>
                    <tr><td className="p-4 font-bold text-black">XL</td><td className="p-4">16</td><td className="p-4">20</td><td className="p-4">48</td><td className="p-4">40-43</td><td className="p-4">32-35</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-8 bg-zinc-50 p-6 border border-gray-100">
                <h4 className="font-bold uppercase tracking-wider text-xs mb-3">How to Measure</h4>
                <ul className="space-y-2 text-sm text-gray-600 font-light">
                  <li className="flex gap-2"><span className="font-medium text-black min-w-[60px]">Bust:</span> Measure around the fullest part of your bust.</li>
                  <li className="flex gap-2"><span className="font-medium text-black min-w-[60px]">Waist:</span> Measure the narrowest part of your torso.</li>
                  <li className="flex gap-2"><span className="font-medium text-black min-w-[60px]">Hips:</span> Measure the fullest part of your hips.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Back Navigation */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3">
          <Link
            href={backLink}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 hover:text-black transition-colors"
          >
            <ChevronLeft size={14} />
            {backLinkText}
          </Link>
        </div>
      </div>

      {/* Main Product Section */}
      <main className="pt-4 lg:pt-6 pb-20 px-4 md:px-6 lg:px-8 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">

          {/* Left Column: Gallery */}
          <div className="lg:col-span-7 flex flex-col-reverse md:flex-row gap-3 lg:gap-4 h-fit lg:sticky lg:top-20">

            {/* Thumbnails */}
            {displayImages.length > 1 && (
              <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible scrollbar-hide">
                {displayImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`relative shrink-0 w-14 h-18 md:w-16 md:h-20 lg:w-18 lg:h-24 border bg-gray-50 transition-all duration-300 ${
                      safeImageIndex === idx
                        ? "border-black opacity-100"
                        : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                  >
                    <Image
                      src={img}
                      alt={`View ${idx + 1}`}
                      fill
                      className="object-contain"
                      unoptimized
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Main Image */}
            <div
              className="flex-1 bg-gray-50 relative group flex items-center justify-center cursor-zoom-in"
              style={{ maxHeight: 'calc(100vh - 120px)' }}
              onClick={() => setIsGalleryModalOpen(true)}
            >
              <div className="w-full h-full flex items-center justify-center" style={{ maxHeight: 'calc(100vh - 120px)' }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={currentImg}
                  alt={product.name}
                  className="w-full h-full object-contain"
                  style={{ maxHeight: 'calc(100vh - 120px)' }}
                />
              </div>
              <div className="absolute bottom-3 right-3 bg-white/80 backdrop-blur px-2 py-1 text-[10px] uppercase tracking-widest">
                {safeImageIndex + 1} / {displayImages.length || 1}
              </div>

              {/* Floating Share Button */}
              <div className="absolute top-3 right-3">
                <ShareButton
                  url={shareUrl}
                  title={product.name}
                  description={product.description || `Check out ${product.name} from ${brand.name}`}
                  variant="ghost"
                  size="icon"
                  className="w-9 h-9 bg-white/80 backdrop-blur-md border border-gray-100 rounded-full shadow-md hover:bg-white"
                />
              </div>
            </div>
          </div>

          {/* Right Column: Details */}
          <div className="lg:col-span-5 flex flex-col h-full">
            <div className="lg:sticky lg:top-20 space-y-5 lg:space-y-6">

              {/* Header */}
              <div className="space-y-1.5 border-b border-gray-100 pb-4">
                <div className="flex justify-between items-start">
                  <span className="text-xs text-gray-500 uppercase tracking-widest">
                    {brand.name}
                  </span>
                  <div className="flex items-center gap-1 text-xs font-medium">
                    <Star size={11} fill="black" stroke="black" />
                    <span>{productRating}</span>
                    <button
                      onClick={() => document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" })}
                      className="text-gray-400 underline decoration-gray-300 underline-offset-2 ml-1.5 hover:text-black transition-colors text-[11px]"
                    >
                      ({reviewCount} Reviews)
                    </button>
                  </div>
                </div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold leading-tight tracking-tight flex items-center gap-3 flex-wrap">
                  {product.name}
                  {canTryOn && (
                    <button
                      onClick={() => setIsTryOnModalOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium uppercase tracking-wider rounded-full transition-colors"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      Virtual Try-On
                    </button>
                  )}
                </h1>
                {/* New or used, in the shop's own words (Bagz Phones, Oct 2026). */}
                <ConditionBadge product={product} size="md" className="mt-1 max-w-full" />
                {/* On sale (Venus, Oct 2026): the old price crossed out and the
                    saving, under the name. The sale price itself is on the button. */}
                {(() => {
                  const off = discountPct(product.price, product.compareAtPrice);
                  return off ? (
                    <p className="mt-2 flex items-center gap-2 text-sm">
                      <span className="font-semibold text-gray-900">UGX {Number(product.price).toLocaleString()}</span>
                      <span className="text-gray-400 line-through">UGX {Number(product.compareAtPrice).toLocaleString()}</span>
                      <span className="rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-white">Save {off}%</span>
                    </p>
                  ) : null;
                })()}
                {/* Free delivery on website orders (Oct 2026). */}
                {product.freeDeliveryOnline && !hireOnlyItem && (
                  <div className="pt-1">
                    <FreeDeliveryBadge className="text-[11px] px-2" />
                  </div>
                )}
              </div>

              {/* Color Selection */}
              {colorOptions.length > 0 && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] uppercase font-bold tracking-widest">Color</span>
                    <span className="text-[11px] text-gray-500 font-light">
                      {selectedColor !== null ? colorOptions[selectedColor].name : "Select Color"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2.5">
                    {colorOptions.map((color: any, idx: number) => (
                      <button
                        key={color.id || idx}
                        onClick={() => handleColorSelect(idx)}
                        className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${selectedColor === idx
                          ? "ring-1 ring-offset-2 ring-black"
                          : "hover:scale-110"
                          }`}
                        aria-label={`Select color ${color.name}`}
                      >
                        <ColorSwatch 
                          hex={color.hex}
                          isMulticolor={color.isMulticolor}
                          multicolorList={color.multicolorList}
                          size={undefined}
                          className="h-full w-full"
                        />
                        {selectedColor === idx && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className="text-[10px] drop-shadow-md font-bold text-white">
                              ✓
                            </span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Design Selection */}
              {designOptions.length > 0 && (
                <div className="space-y-2 pt-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] uppercase font-bold tracking-widest">Design / Pattern</span>
                    <span className="text-[11px] text-gray-500 font-light">
                      {selectedDesignId ? designOptions.find(d => d.id === selectedDesignId)?.name : "Select Design"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {designOptions.map((design) => {
                      const colorId = selectedColor !== null ? colorOptions[selectedColor]?.id : null;
                      const lookId = colorId && design.id ? `${colorId}:${design.id}` : null;
                      const hasImagesForLook = lookId && (product.fashionAttributes?.additionalVariantImages?.[lookId]?.length ?? 0) > 0;

                      return (
                        <button
                          key={design.id}
                          onClick={() => handleDesignSelect(design.id)}
                          className={`relative px-4 h-9 flex items-center justify-center text-[10px] uppercase tracking-widest font-bold transition-all duration-300 border
                            ${selectedDesignId === design.id
                              ? "bg-black text-white border-black shadow-lg shadow-black/10"
                              : "bg-white text-gray-500 border-gray-100 hover:border-gray-300 hover:text-black"
                            }`}
                        >
                          {design.name}
                          {hasImagesForLook && selectedDesignId !== design.id && (
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full border border-white" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* One clothing or shoe size: nothing to choose, but a shopper
                  still needs to know it's an M. Any other single size (decor,
                  flowers, "One Size") shows nothing. */}
              {singleSize && showSizeGuide && (
                <div className="flex justify-between items-center gap-3">
                  <span className="text-[11px] uppercase font-bold tracking-widest">Size</span>
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-black font-medium">{availableSizes[0]}</span>
                    <button
                      onClick={() => setIsSizeGuideOpen(true)}
                      className="text-[11px] text-gray-500 underline decoration-gray-300 hover:text-black transition-colors"
                    >
                      Size Guide
                    </button>
                  </div>
                </div>
              )}

              {/* Size Selection — only when there is more than one size to pick */}
              {availableSizes.length > 0 && !singleSize && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] uppercase font-bold tracking-widest">
                      {isFood ? "Portion" : isElectronics ? (ecAttrs.variantAxisLabel || "Storage") : "Size"}
                    </span>
                    {showSizeGuide && (
                      <button
                        onClick={() => setIsSizeGuideOpen(true)}
                        className="text-[11px] text-gray-500 underline decoration-gray-300 hover:text-black transition-colors"
                      >
                        Size Guide
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-5 gap-1.5">
                    {availableSizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`h-8 border flex items-center justify-center text-xs transition-all duration-200 
                          ${selectedSize === size
                            ? "bg-black text-white border-black"
                            : "bg-white text-black border-gray-200 hover:border-black"
                          }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* For hire (2026-09): rates, deposit, what comes with it, Request to hire. */}
              <HirePanel product={product as any} />

              {/* What it comes with, and the extras to add (Oct 2026). A hire
                  item's panel above already lists the kit; a price on request
                  has nothing to add to a cart. */}
              {!hireOnlyItem && addOnPieces.length > 0 && (
                <AddOnsPicker
                  pieces={addOnPieces}
                  value={addOnSelection}
                  onChange={setAddOnSelection}
                  itemQuantity={quantity}
                  showFree={!isForHire(product)}
                  showPaid={offersPaidAddOns}
                  currency={(product as any).currency || "UGX"}
                />
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-3">
                {hireOnlyItem ? null : shouldHidePrice ? (
                  <button
                    onClick={() => setIsInquiryOpen(true)}
                    className="flex-1 h-12 bg-[#075E54] text-white uppercase tracking-widest font-medium text-xs flex items-center justify-center gap-2 transition-all duration-300 hover:bg-[#064E46]"
                  >
                    <MessageCircle size={16} />
                    <span>Enquire Now</span>
                  </button>
                ) : !canPurchase ? (
                  <button
                    disabled
                    className="flex-1 h-12 bg-gray-200 text-gray-400 uppercase tracking-widest font-medium text-xs flex items-center justify-center cursor-not-allowed"
                  >
                    {/* Say WHY it cannot be ordered: a menu item the house took
                        off for today is not "sold out" (the card already said
                        "Off menu today"; this button said Sold Out for both). */}
                    {offMenu ? "Off menu today" : "Sold Out"}
                  </button>
                ) : (
                  /* Smart Add to Cart button — always clickable, label guides user */
                  <button
                    onClick={handleAddToCart}
                    disabled={isAdding}
                    className={`flex-1 h-12 uppercase tracking-widest font-medium text-xs flex items-center justify-center gap-2 transition-all duration-300 ${
                      isAdding
                        ? "bg-black/70 text-white scale-95"
                        : (colorOptions.length > 0 && selectedColor === null) || (availableSizes.length > 0 && !selectedSize)
                          ? "bg-gray-900 text-white hover:bg-black"
                          : "bg-black text-white hover:bg-zinc-800"
                    }`}
                  >
                    {isAdding ? (
                      <span className="animate-pulse">Adding...</span>
                    ) : colorOptions.length > 0 && selectedColor === null ? (
                      <span>Choose a Colour</span>
                    ) : designOptions.length > 0 && !selectedDesignId ? (
                      <span>Choose a Design</span>
                    ) : availableSizes.length > 0 && !selectedSize ? (
                      <span>{isFood ? "Choose a Portion" : "Choose a Size"}</span>
                    ) : (
                      <>
                        <ShoppingBag size={15} />
                        <span>{isPreorder && !stockStatus ? "Pre-order" : "Add to Cart"}</span>
                        <span className="w-px h-3 bg-white/20 mx-1.5" />
                        {/* With the extras ticked, per item. */}
                        <span>UGX {((Number(getCurrentPrice()) || 0) + extrasPerItem).toLocaleString()}</span>
                      </>
                    )}
                  </button>
                )}
                <button className="w-12 h-12 border border-gray-200 flex items-center justify-center hover:border-black hover:bg-gray-50 transition-colors">
                  <Heart size={18} />
                </button>
              </div>

              {/* Free delivery, and the rule when the bag mixes (Oct 2026). */}
              {product.freeDeliveryOnline && !hireOnlyItem && <FreeDeliveryNote />}

              {/* Product Description */}
              {product.description && (
                <div className="space-y-2 pt-4 border-t border-gray-100">
                  <h3 className="text-[11px] uppercase font-bold tracking-widest">Product Description</h3>
                  <div className="relative">
                    <p className={`text-gray-600 leading-relaxed font-light text-sm ${!isDescriptionExpanded ? 'line-clamp-3' : ''}`}>
                      {product.description}
                    </p>
                    {product.description.length > 150 && (
                      <button
                        onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                        className="text-xs text-black underline decoration-gray-300 hover:decoration-black transition-colors mt-1"
                      >
                        {isDescriptionExpanded ? 'Read Less' : 'Read More'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Size & measurements (Events & Decor and anything that has them) */}
              {measureRows.length > 0 && (
                <div className="pt-5 border-t border-gray-100">
                  <h3 className="text-[11px] uppercase font-bold tracking-widest mb-4 flex items-center gap-2">
                    <Ruler className="w-3.5 h-3.5" />
                    Size &amp; measurements
                  </h3>
                  <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
                    {measureRows.map((row, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-3 px-4 py-3 ${idx % 2 === 0 ? "bg-gray-50/60" : "bg-white"} ${idx !== measureRows.length - 1 ? "border-b border-gray-100" : ""}`}
                      >
                        <span className="text-xs text-gray-500 font-medium min-w-[110px] flex-shrink-0">
                          {measurementLabel(row.type)}
                          {row.size ? ` · ${row.size}` : ""}
                        </span>
                        <span className="text-xs text-gray-900 font-semibold">{formatMeasurementValue(row.value, row.unit)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Electronics Specifications — Premium card design */}
              {isElectronics && specEntries.length > 0 && (
                <div className="pt-5 border-t border-gray-100">
                  <h3 className="text-[11px] uppercase font-bold tracking-widest mb-4 flex items-center gap-2">
                    <Cpu className="w-3.5 h-3.5" />
                    Specifications
                  </h3>
                  <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
                    {specEntries.map((spec, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-3 px-4 py-3 ${idx % 2 === 0 ? "bg-gray-50/60" : "bg-white"
                          } ${idx !== specEntries.length - 1 ? "border-b border-gray-100" : ""}`}
                      >
                        <span className="text-gray-400 flex-shrink-0">{spec.icon}</span>
                        <span className="text-xs text-gray-500 font-medium min-w-[110px] flex-shrink-0">{spec.label}</span>
                        <span className="text-xs text-gray-900 font-semibold">
                          {spec.value}
                          {spec.varies && <span className="font-normal text-gray-400"> · depends on the option you pick</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Accordion Details — Only show tabs that have actual data */}
              {productDetails.length > 0 && (
                <div className="pt-4 border-t border-gray-100">
                  {productDetails.map((detail, idx) => (
                    <div key={idx} className="border-b border-gray-100 last:border-0">
                      <button
                        onClick={() => setOpenAccordion(openAccordion === idx ? -1 : idx)}
                        className="w-full py-3 flex justify-between items-center text-left group"
                      >
                        <span className="text-xs font-bold uppercase tracking-widest">{detail.title}</span>
                        <span className={`transition-transform duration-300 ${openAccordion === idx ? "rotate-180" : "rotate-0"}`}>
                          {openAccordion === idx ? <Minus size={14} /> : <Plus size={14} />}
                        </span>
                      </button>
                      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${openAccordion === idx ? "max-h-32 opacity-100 mb-3" : "max-h-0 opacity-0"
                        }`}>
                        <p className="text-xs text-gray-600 font-light leading-relaxed pr-4">
                          {detail.content}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        </div>
      </main>

      {/* Reviews Section */}
      <section className="py-16 lg:py-24 bg-zinc-50 border-y border-gray-100" id="reviews">
        <div className="container mx-auto px-4 md:px-12 max-w-[1200px]">
          <div className="text-center mb-12 lg:mb-16">
            <div className="flex justify-center items-center gap-2 mb-4">
              <StarRating rating={productRating} size={16} readonly />
              <span className="text-sm font-bold tracking-widest">{productRating.toFixed(1)} / 5.0</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2">Customer Reviews</h3>
            <p className="text-gray-500 font-light">Thoughts from our customers.</p>
          </div>

          {/* Write Review Form */}
          {isWritingReview && (
            <div className="mb-12">
              <WriteReviewForm
                onSubmit={handleSubmitReview}
                onCancel={() => setIsWritingReview(false)}
              />
            </div>
          )}

          {/* Write Review Button */}
          {!isWritingReview && (
            <div className="text-center mb-12">
              <button
                onClick={() => setIsWritingReview(true)}
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] border border-black px-8 py-4 hover:bg-black hover:text-white transition-all duration-300"
              >
                Write a Review
              </button>
            </div>
          )}

          {/* Reviews Grid */}
          {productReviews.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
                {(reviewsExpanded ? productReviews : productReviews.slice(0, 4)).map((review: any) => (
                  <ReviewCard
                    key={review._id}
                    review={review}
                    currentUserId="guest"
                    onVote={handleVote}
                  />
                ))}
              </div>

              {productReviews.length > 4 && (
                <div className="text-center">
                  <button
                    onClick={() => setReviewsExpanded(!reviewsExpanded)}
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] border border-black px-8 py-4 hover:bg-black hover:text-white transition-all duration-300"
                  >
                    {reviewsExpanded ? "Show Less" : "Load More Reviews"}
                    <span className={`transition-transform duration-300 ${reviewsExpanded ? "rotate-180" : ""}`}>
                      <ChevronDown size={14} />
                    </span>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-4">No reviews yet.</p>
              <p className="text-sm">Be the first to review this product!</p>
            </div>
          )}
        </div>
      </section>

      {/* More in <collection> (Sep 2026): the shop's own groups this product
          is in, before everything else from the brand. */}
      {collectionSections.map((section) => (
        <section key={section._id} className="pt-16 lg:pt-20 px-4 md:px-12 max-w-[1800px] mx-auto">
          <div className="flex justify-between items-end mb-8 lg:mb-12 gap-4">
            <h3 className="text-xl lg:text-2xl font-bold uppercase tracking-tight">More in {section.name}</h3>
            <Link
              href={`${productUrlPrefix}/collections/${section.slug}`}
              className="shrink-0 flex items-center gap-2 text-xs lg:text-sm font-bold uppercase tracking-widest hover:underline underline-offset-4"
            >
              See the collection <ChevronRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {section.products.map((item) => (
              <ShopProductCard
                key={item._id}
                industry={item.industry || industry}
                productUrlPrefix={productUrlPrefix}
                product={toCardProduct(item)}
              />
            ))}
          </div>
        </section>
      ))}

      {/* More From Brand */}
      {moreFromBrand.length > 0 && (
        <section className="py-16 lg:py-20 px-4 md:px-12 max-w-[1800px] mx-auto">
          <div className="flex justify-between items-end mb-8 lg:mb-12">
            <div>
              <h3 className="text-xl lg:text-2xl font-bold uppercase tracking-tight">
                More from {brand.name}
              </h3>
            </div>
            <div className="hidden md:flex items-center gap-4">
              {moreFromBrand.length > 3 && (
                <div className="shrink-0">
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full w-10 h-10 border-gray-200 hover:border-black"
                    onClick={scrollBrandLeft}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full w-10 h-10 border-gray-200 hover:border-black"
                    onClick={scrollBrandRight}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              )}
              <Link
                href={brandPageUrl || `/brands/${brand.slug || brand.subdomain || brand.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="hidden lg:flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:underline underline-offset-4"
              >
                Shop all <ChevronRight size={16} />
              </Link>
            </div>
          </div>

          <div
            ref={brandScrollContainerRef}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6"
          >
            {/* Reuse the EXACT same card the main shop grid uses
                (ShopProductCard → Fashion/Electronics), fed the same shape so
                stock, variants, price and the Buy/wishlist actions all behave
                identically. The related-products query now returns live stock,
                so the in-stock state is correct (was previously always "out of
                stock"). */}
            {moreFromBrand.map((item) => (
              <ShopProductCard
                key={item._id}
                industry={item.industry || industry}
                productUrlPrefix={productUrlPrefix}
                product={toCardProduct(item)}
              />
            ))}
          </div>

          <div className="mt-8 lg:mt-12 text-center md:hidden">
            <Link
              href={brandPageUrl || `/brands/${brand.slug || brand.subdomain || brand.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest border-b border-black pb-1"
            >
              Shop all <ChevronRight size={16} />
            </Link>
          </div>
        </section>
      )}

      {/* Custom scrollbar hide styles */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      {/* Virtual Try-On Modal — things a person wears only */}
      {canTryOn && (
        <VirtualTryOnModal
          isOpen={isTryOnModalOpen}
          onClose={() => setIsTryOnModalOpen(false)}
          brandId={brand._id as string}
          product={{
            _id: product._id,
            name: product.name,
            images: product.images,
            description: product.description,
          }}
        />
      )}

      {/* Gallery Lightbox Modal */}
      {isGalleryModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-white/95 backdrop-blur-xl animate-in fade-in duration-300">
          <button
            onClick={() => setIsGalleryModalOpen(false)}
            className="absolute top-6 right-6 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-[210]"
          >
            <X size={24} />
          </button>

          <div className="relative w-full h-full flex flex-col items-center justify-center p-4 md:p-12">
          <div className="relative w-full max-w-4xl h-[70vh] flex flex-col items-center justify-center">
            {/* Design Switching Tabs in Modal */}
            {designOptions.length > 0 && (
              <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 flex gap-4 bg-gray-50/50 backdrop-blur-md p-1 rounded-full border border-gray-100 z-[220]">
                {designOptions.map((design) => (
                  <button
                    key={design.id}
                    onClick={() => handleDesignSelect(design.id)}
                    className={`px-6 py-2 rounded-full text-[10px] uppercase tracking-widest font-bold transition-all duration-300 ${
                      selectedDesignId === design.id
                        ? "bg-black text-white shadow-lg scale-105"
                        : "text-gray-400 hover:text-black"
                    }`}
                  >
                    {design.name}
                  </button>
                ))}
              </div>
            )}

            {/* Navigation Arrows */}
              {displayImages.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage((prev) => (prev - 1 + displayImages.length) % displayImages.length);
                    }}
                    className="absolute left-0 p-3 rounded-full bg-white/50 backdrop-blur hover:bg-white shadow-lg transition-all z-10"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage((prev) => (prev + 1) % displayImages.length);
                    }}
                    className="absolute right-0 p-3 rounded-full bg-white/50 backdrop-blur hover:bg-white shadow-lg transition-all z-10"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}

              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={displayImages[selectedImage] || displayImages[0]}
                alt={product.name}
                className="max-w-full max-h-full object-contain animate-in zoom-in-95 duration-500"
              />
            </div>

            {/* Lightbox Thumbnails */}
            {displayImages.length > 1 && (
              <div className="mt-8 flex justify-center gap-3 overflow-x-auto max-w-full p-2">
                {displayImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`relative w-16 h-20 border-2 transition-all duration-300 rounded ${selectedImage === idx ? "border-black scale-110 shadow-md" : "border-transparent opacity-50 hover:opacity-100"
                      }`}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={img}
                      alt={`Thumb ${idx + 1}`}
                      className="w-full h-full object-cover rounded-[1px]"
                    />
                  </button>
                ))}
              </div>
            )}
            
            <div className="mt-6 text-[10px] uppercase tracking-[0.3em] font-bold text-gray-400">
              {selectedImage + 1} / {displayImages.length}
            </div>
          </div>
        </div>
      )}

      {/* Product Inquiry Dialog — for hidden/custom price products */}
      <ProductInquiryDialog
        isOpen={isInquiryOpen}
        onClose={() => setIsInquiryOpen(false)}
        productName={product.name}
        productImage={product.images?.[0]}
        brandName={brand.name}
        brandId={brand._id}
        productId={product._id}
      />
    </div>
  );
}
